import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Globe } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import Brand from "@/components/Brand";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage(t("verify_missing_token"));
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(async () => {
        setStatus("success");
        await refresh();
        setTimeout(() => navigate("/"), 2500);
      })
      .catch((err) => {
        setStatus("error");
        const detail = err.response?.data?.detail;
        setMessage(typeof detail === "string" ? detail : t("verify_failed"));
      });
  }, [params, navigate, refresh, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-pattern px-4">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-verify-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>
      <div className="bg-white rounded-2xl card-shadow p-8 max-w-md w-full text-center" data-testid="verify-email-page">
        <Brand variant="hero" className="mb-4" />
        {status === "loading" && (
          <>
            <p className="text-gray-600">{t("verifying_email")}…</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="bg-emerald-50 text-emerald-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="text-xl font-bold text-emerald-800 mb-2">{t("verify_success_title")}</h2>
            <p className="text-gray-600 text-sm mb-2">{t("verify_success_msg")}</p>
            <p className="text-xs text-gray-400">{t("redirecting")}…</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="bg-rose-50 text-rose-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <XCircle size={32} />
            </div>
            <h2 className="text-xl font-bold text-rose-800 mb-2">{t("verify_failed_title")}</h2>
            <p className="text-gray-600 text-sm mb-4">{message}</p>
            <Link to="/login" className="btn btn-primary inline-flex" data-testid="verify-back-to-login">
              {t("back_to_login")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
